/**
 * Microphone permission, acquired at the right moment.
 *
 * iOS only honours a getUserMedia prompt while a user gesture is still
 * "active", and a gesture does not survive a network round trip. The host flow
 * taps Go live -> POST /api/host/show -> room.connect -> publish, by which
 * point the tap is long gone and the prompt is either suppressed or refused.
 * Calling this FIRST, synchronously inside the handler, moves the prompt back
 * under the tap that caused it.
 *
 * The live track is RETURNED, not released.
 *
 * Releasing it and letting LiveKit call getUserMedia again is what a first
 * version did, and it is wrong on iOS: acquiring, stopping, and re-acquiring
 * the same microphone in quick succession hands back a track that is live and
 * unmuted and carries pure silence. The SFU shows a healthy audio track,
 * everyone sees you unmuted, and nobody can hear you — indistinguishable from
 * the app working. So the ONE track opened under the tap is the track that gets
 * published.
 *
 * The caller owns it from here: publish it, or stop it if joining fails, or the
 * recording indicator stays lit on a phone that is not in a call.
 */
export async function primeMicrophone(): Promise<
  { ok: true; track: MediaStreamTrack } | { ok: false; blocked: boolean; message: string }
> {
  if (!navigator.mediaDevices?.getUserMedia) {
    return {
      ok: false,
      blocked: false,
      message: 'This browser cannot open a microphone. Try Safari or Chrome.',
    };
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      // The same constraints Room's audioCaptureDefaults would have applied,
      // since LiveKit is no longer the one opening the device.
      audio: { autoGainControl: true, echoCancellation: true, noiseSuppression: true },
    });
    const [track] = stream.getAudioTracks();
    if (!track) {
      for (const t of stream.getTracks()) t.stop();
      return { ok: false, blocked: false, message: 'No audio track from the microphone.' };
    }
    return { ok: true, track };
  } catch (error) {
    const name = error instanceof DOMException ? error.name : '';
    // NotAllowed is a decision; NotFound/NotReadable are the device. They need
    // different advice, and "check your settings" is useless for a busy mic.
    const blocked = name === 'NotAllowedError' || name === 'SecurityError';
    return {
      ok: false,
      blocked,
      message: blocked
        ? 'Microphone access is blocked. Allow it for this site, then tap Go live again.'
        : name === 'NotFoundError'
          ? 'No microphone found on this device.'
          : 'Another app is using the microphone. Close it and try again.',
    };
  }
}
